import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { UpdateOrderRequest, NEW_DESIGN_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { Promotion } from '@css-xp/graphtypes';
import {
  useSidebar,
  useFormSubmit,
  useNotification,
  EntitlementIcon,
  NotificationTypes,
  successNotificationBuilder,
  SummarySectionV2,
  CopyableText,
  formattedV2,
  InfoPanel,
  ErrorComponents,
} from '@css-xp/base';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { OMSMetadata } from '@atlassian/cssxp-types-entitlement';
import { withApollo } from '@css-xp/base/graphql-provider';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection, Field, ErrorMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { capitalize } from 'lodash';
import './optout-trial-pane.scss';

interface Props {
  entitlementId: string;
  entitlementNumber: EntitlementDetails['entitlementNumber'];
  entitlement: EntitlementDetails;

  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

interface SelectItem {
  label: string;
  value: string;
}

interface FormData {
  offeringSelectedItem?: OfferingSelectItem;
  supportTicketNumber?: string | null;
  handNote?: string | null;
  pricingPlan?: string;
  promotionId?: { promotion: Promotion };
  billingFrequency?: SelectItem;
  reasonCode?: string;
}

const getNotificationContent = (upgradeTrial: boolean) => {
  return (
    <div>
      {upgradeTrial ? (
        <p>
          The subscription has reverted to standard version. You can view the changes in <strong>Order history</strong>
        </p>
      ) : (
        <p>
          The subscription has been changed. A new invoice will be available in 24 hours. You can view the changes in{' '}
          <strong>Order history</strong>
        </p>
      )}
      <Link to={`#order-history`}>View order history</Link>
    </div>
  );
};

export const OptoutTrial = ({ entitlementId, entitlementNumber, entitlement, refetch }: Props) => {
  const { closeSidebar } = useSidebar();

  const productName = entitlement.productName;
  const productId = entitlement.productId;
  const isCloud = entitlement.platform?.trim().toUpperCase() === 'CLOUD';
  const isOnUpgradeTrial = !!entitlement.upgradeTrial;

  const header = () => {
    return (
      <div className="header-box">
        <EntitlementIcon productId={productId ?? ''} className="avatar medium" isCloud={isCloud} />
        <h4 className="product-header">{`${productName} ${entitlementNumber}`}</h4>
      </div>
    );
  };

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const getNotificationOption = (id: string, header: string, upgradeTrial: EntitlementDetails['upgradeTrial']) => {
    const notificationOptions = successNotificationBuilder();

    return notificationOptions
      ?.withId(id)
      .withHeader(header)
      .withMessage(getNotificationContent(!!upgradeTrial))
      .withDismissAfter(3000)
      .isVersion(NotificationTypes.FLAG)
      .build();
  };
  if (complete && !notificationSent) {
    const notificationOptions = getNotificationOption(
      'optout-trial-success',
      'Opt out from trial completed',
      entitlement.upgradeTrial
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const constructRequestData = (entitlementId: string, formData: FormData) => {
    const requestData: UpdateOrderRequest = {
      metadata: constructMetadata(formData),
      entitlementId: entitlementId,
      isOptOutTrial: true,
      productId: entitlement?.productId ?? '',
      units: entitlement.activeUsers?.toString() ?? '',
    };
    return requestData;
  };

  const constructMetadata = (formData: FormData) => {
    const metadata: Record<string, string> = {};
    if (formData.handNote) {
      metadata[OMSMetadata.HAND_NOTE] = formData.handNote;
    }
    if (formData.supportTicketNumber) {
      metadata[OMSMetadata.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
    }
    return metadata;
  };

  const handleFormSubmit = (formData: FormData) => {
    const requestData = constructRequestData(entitlementId, formData);
    submitForm(requestData);
  };

  return (
    <div className="optout-trial-container">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty }) => (
          <form {...formProps}>
            <FormHeader title={'Opt out of trial'} description={header()}></FormHeader>
            {!!formError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to opt out for the following reason"
                    message={formError || 'Something went wrong.'}
                  />
                }
              />
            )}
            <div className="warning-container" data-testid="warning-container">
              <SectionMessage appearance="warning">
                {!isOnUpgradeTrial ? (
                  <p>
                    The trial opt-out, and recharge to the customer, will be immediate. The customer will be charged for
                    their Standard plan from the Premium trial start date -{' '}
                    <formattedV2.FormattedDate
                      dateFormat={NEW_DESIGN_DATE_FORMAT}
                      date={
                        entitlement?.subscriptionTimestamps?.firstTrialStartTimestamp ||
                        entitlement?.latestPeriodStartDate
                      }
                    />
                  </p>
                ) : (
                  <p>
                    The trial opt-out will be immediate. The customer will contine to be charged for their Standard
                    plan.
                  </p>
                )}
              </SectionMessage>
            </div>
            <SummarySectionV2.SummarySection columns="200px 200px 200px" className="optout-trial-summary">
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="last-bill-date">
                  {!isOnUpgradeTrial ? (
                    <>
                      <label>Last bill date</label>
                      <div>
                        {entitlement?.latestPeriodStartDate ? (
                          <CopyableText>
                            <formattedV2.FormattedDate
                              dateFormat={NEW_DESIGN_DATE_FORMAT}
                              date={entitlement?.latestPeriodStartDate}
                            />
                          </CopyableText>
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <label>Current plan</label>

                      <div>{entitlement?.offering?.name ? <p>{entitlement.offering.name}</p> : 'N/A'}</div>
                    </>
                  )}
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="plan">
                  {!isOnUpgradeTrial ? (
                    <>
                      <label>Plan</label>
                      <div>{entitlement?.offering?.name ?? 'N/A'}</div>
                    </>
                  ) : (
                    <>
                      <label>Trial plan</label>
                      <div>{entitlement?.upgradeTrial?.offeringName ?? 'N/A'}</div>
                    </>
                  )}
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="billing-cycle">
                  <label>Billing cycle</label>
                  <div>
                    <span>
                      {entitlement?.billingFrequency &&
                        entitlement?.billingFrequency !== 'OTHER' &&
                        `${capitalize(entitlement?.billingFrequency)} `}
                    </span>
                  </div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            </SummarySectionV2.SummarySection>
            <FormSection>
              <Field
                name="supportTicketNumber"
                label={'Support ticket number'}
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      name="supportTicketNumber"
                      placeholder={'Add GSAC ticket number'}
                      testId="support-ticket-number"
                    />
                    {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormSection>
              <Field
                label={'Internal comment'}
                name="handNote"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield {...fieldProps} name="handNote" testId="hand-note" placeholder={'Add comment'} />
                    {error && <ErrorMessage>This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <span className="form-footer-btn-wrapper">
                <ButtonGroup>
                  <Button
                    appearance="subtle"
                    onClick={() => closeSidebar()}
                    testId="cancel"
                    className="close-sidebar-btn"
                  >
                    Close
                  </Button>
                  <LoadingButton
                    appearance={'warning'}
                    type="submit"
                    isLoading={isProcessing}
                    testId={'optout-trial'}
                    aria-label="Optout trial"
                    isDisabled={!dirty}
                  >
                    Opt out
                  </LoadingButton>
                </ButtonGroup>
              </span>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default withApollo(OptoutTrial);
